import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { TableColumns } from '../../_models/components/table-columns.model';
import { FilterMetadata, LazyLoadEvent, MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';

import { TableMetaData } from '../../_models/components/table-meta-data.model';
import {
  ActiveFilter,
  FILTER_OPERATORS,
  translateFiltersToQueryParams
} from '../../_models/components/table-filter.model';
import { ApiService } from 'src/app/_core/_services/api.service';
import { Menu } from 'primeng/menu';
import { Router, ActivatedRoute } from '@angular/router';
import { ShareTableService } from '../../_services/share-table.service';
import {
  statusSeverity,
  tocTypeTagStyle,
  TOC_SEQUENCE_TAG_STYLE
} from '../../_utils/status-severity';

interface FilterObject {
  [key: string]: { value: string | Date | null; matchMode: string; operator: string }[];
}

@Component({
  selector: 'share-table',
  templateUrl: './share-table.component.html',
  styleUrls: ['./share-table.component.scss']
})
export class ShareTableComponent implements OnInit, OnDestroy {
  // definitions
  // ===========

  // properties and configuration
  // ----------------------------

  @ViewChild('menu') menu!: Menu;
  @ViewChild('cm') contextMenu!: any;
  @ViewChild('shareTable') primeTable!: Table;

  // Responsive properties
  isSmallScreen: boolean = false;
  private resizeListener: (() => void) | undefined;

  tocTypeTagStyle = tocTypeTagStyle;
  tocSequenceTagStyle = TOC_SEQUENCE_TAG_STYLE;
  readonly TOC_SEQUENCE_DEFAULT = 'toc1';

  @Input() enableContextMenu?: boolean = true;
  @Input() queryParams?: string = '';
  @Input() serviceMenuItem?: MenuItem[] = [];
  @Input() menuItems: MenuItem[] = [];
  @Input() tableId?: string = '';
  /** Base API endpoint used to build the /download URL, e.g. 'portal/management/customers'. */
  @Input() downloadEndpoint?: string;
  /** Show/hide the download button. Requires downloadEndpoint to be set. */
  @Input() enableDownloads: boolean = false;
  /** Formats to offer as download buttons. Each renders a separate button. Defaults to ['csv']. */
  @Input() downloadFormats: ('csv' | 'xlsx')[] = ['csv'];
  // RSQL filters the parent applies outside the table (e.g. Type/Tag
  // pickers above it), so a download matches what the list shows.
  @Input() extraFilters: string[] = [];

  // private
  // -------

  refreshTrigger: any;
  filterValue: string = '';
  filteringFor: string = '';
  private subscriptions: Subscription[] = [];

  // Bound to p-table so a restored saved sort actually renders as sorted
  // (the header indicator and the emitted query were otherwise out of sync).
  sortField: string = '';
  sortOrder: number = 1;

  private _metaData: TableMetaData = {
    totalRecords: 0,
    rows: 100,
    first: 0,
    loading: true,
    globalFilter: '',
    sort: '',
    id: ''
  };

  set metaData(value: TableMetaData) {
    // When the parent passes globalFilter: '' on initial binding, apply the saved
    // preference instead. This prevents an unfiltered backend request from racing
    // with the preferences-restored filtered emit in ngOnInit.
    if (this.tableId && !value.globalFilter) {
      const savedFilter = this.shareTableService.getPreferences(this.tableId)?.globalFilter;
      if (savedFilter) {
        value = { ...value, globalFilter: savedFilter };
      }
    }

    // Same race, for sort: this setter fires (and re-emits) on the very first
    // @Input binding, before ngOnInit/p-table's own lazy-load has restored the
    // saved sort. Without backfilling here too, that first emit goes out
    // unsorted, and whichever of it or the later, correctly-sorted emit
    // responds last "wins" in the UI - the column header's sort indicator
    // stays correct regardless (bound separately via sortField/sortOrder), so
    // the mismatch is easy to miss visually.
    if (this.tableId && !value.sort) {
      const savedPreferences = this.shareTableService.getPreferences(this.tableId);
      if (savedPreferences?.sortField) {
        value = {
          ...value,
          sort: this.convertSortFieldAndOrder(
            savedPreferences.sortField,
            savedPreferences.sortOrder ?? 1
          )
        };
      }
    }

    this._metaData = value;
    this.totalRecords = value.totalRecords;

    if (value.globalFilter !== undefined) {
      this.globalFilterValue = value.globalFilter;
    }

    this.metaDataChange.emit(value);

    if (this.tableId) {
      this.shareTableService.updateMetadata(this.tableId, value);
    }
  }

  // inputs
  // ------

  @Input() get metaData(): TableMetaData {
    return this._metaData;
  }
  @Input() data: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  @Input() columns: TableColumns[] = [];
  @Input() showAction: boolean = false;

  visibleColumns: string[] = [];

  // outputs
  // -------

  @Output() metaDataChange: EventEmitter<TableMetaData> = new EventEmitter<TableMetaData>();
  @Output() selectedRowCMObservable$: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedRowObservable$: EventEmitter<any> = new EventEmitter<any>();

  @Output() selectedRelatedEntityObservable$: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedFilterObservable$: EventEmitter<any> = new EventEmitter<any>();
  @Output() showPaymentDialog: EventEmitter<any> = new EventEmitter<any>();

  // public
  totalRecords: number = 0;
  selectedRow: any;
  selectedRowCM: any;
  globalFilterValue: string = '';
  filter: any;
  downloading = false;
  loadingRowsNumbers = Array(15)
    .fill(1, 0, 15)
    .map((x, i) => i); // [0,1,2,3,4]

  // Complex filtering
  private firstLazyLoadCompleted = false;
  activeFilters: ActiveFilter[] = [];
  filterBuilderVisible = false;
  filterLogic: 'AND' | 'OR' = 'AND';
  pendingField = '';
  pendingOperator = 'contains';
  pendingValue = '';
  readonly operatorOptions = [...FILTER_OPERATORS];
  readonly logicOptions = [
    { label: 'AND', value: 'AND' },
    { label: 'OR', value: 'OR' }
  ];

  // A chip with a route navigates on its own; the row click must not fire.
  onChipClick(event: Event, route?: string): void {
    if (!route) return;
    event.stopPropagation();
    this.router.navigateByUrl(route);
  }

  constructor(
    private api: ApiService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private shareTableService: ShareTableService
  ) {}

  ngOnInit(): void {
    // Initialize responsive behavior
    this.checkScreenSize();
    this.setupResizeListener();

    if (this.tableId) {
      if (!this.shareTableService.getTableInstance(this.tableId)) {
        this.shareTableService.registerTable(this.tableId, this.columns ?? []);
      }
      this.loadPreferencesFromService();

      // Directly emit saved filters so the parent reloads with the correct filter
      // without relying on loadDataLazy's hasChanged check (which may be false
      // when the singleton metaDataEntityMap already carries the same filter value).
      if (this.metaData && (this.activeFilters.length > 0 || this.globalFilterValue?.trim())) {
        this.emitWithFilters();
      }
    }

    // Wait for columns to populate
    this.initializeVisibleColumns();
  }

  ngOnDestroy(): void {
    // Unregister table from service
    if (this.tableId) {
      this.shareTableService.unregisterTable(this.tableId);
    }

    // Clean up subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  ngOnChanges(changes: any): void {
    // Handle column changes
    if (changes['columns'] && changes['columns'].currentValue) {
      // Update columns in service
      if (this.tableId) {
        this.shareTableService.updateColumns(this.tableId, changes['columns'].currentValue);
      }

      this.initializeVisibleColumns();
    }

    // Handle table ID changes (component instance reused when switching tabs)
    if (changes['tableId'] && !changes['tableId'].firstChange && changes['tableId'].currentValue) {
      this.firstLazyLoadCompleted = false;
      if (this.columns.length > 0) {
        this.shareTableService.registerTable(changes['tableId'].currentValue, this.columns);
      }
      this.loadPreferencesFromService();
      // Re-emit so the parent reloads data with the restored filter/sort state for the new tab
      this.emitWithFilters();
    }
  }

  getTruncatedSelection(): string {
    const selected = this.columns
      .filter((col) => col.header)
      .map((col) => col.header)
      .join(', ');
    return selected.length > 20 ? selected.substring(0, 20) + '...' : selected;
  }

  initializeVisibleColumns(): void {
    // Check if columns exist and have values before setting visibleColumns
    if (this.columns?.length) {
      // Register table with service if not already registered
      if (this.tableId) {
        const existingInstance = this.shareTableService.getTableInstance(this.tableId);
        if (!existingInstance) {
          this.shareTableService.registerTable(this.tableId, this.columns);
        }

        this.loadPreferencesFromService();
        if (
          this.firstLazyLoadCompleted &&
          (this.activeFilters.length > 0 || this.globalFilterValue?.trim())
        ) {
          this.emitWithFilters();
        }
      } else {
        this.visibleColumns = this.columns.map((col) => col.field);
      }
    } else {
      // If columns are loaded asynchronously, watch for changes
      const columnsWatcher = setInterval(() => {
        if (this.columns?.length) {
          if (this.tableId) {
            const existingInstance = this.shareTableService.getTableInstance(this.tableId);
            if (!existingInstance) {
              this.shareTableService.registerTable(this.tableId, this.columns);
            }
            this.loadPreferencesFromService();
            if (
              this.firstLazyLoadCompleted &&
              (this.activeFilters.length > 0 || this.globalFilterValue?.trim())
            ) {
              this.emitWithFilters();
            }
          } else {
            this.visibleColumns = this.columns.map((col) => col.field);
          }
          clearInterval(columnsWatcher);
          this.cdr.detectChanges();
        }
      }, 100);

      // Clear watcher after 10 seconds to prevent memory leaks
      setTimeout(() => {
        clearInterval(columnsWatcher);
      }, 10000);
    }
  }

  /**
   * Check if the current screen size is small (tablet or phone)
   */
  private checkScreenSize(): void {
    this.isSmallScreen = window.innerWidth < 768; // 768px is typical tablet breakpoint
  }

  /**
   * Setup resize listener to update responsive behavior
   */
  private setupResizeListener(): void {
    this.resizeListener = () => {
      this.checkScreenSize();
      this.cdr.detectChanges();
    };
    window.addEventListener('resize', this.resizeListener);
  }

  /**
   * Handle action button click for small screens
   * @param event - The click event
   * @param entity - The row data
   */
  onActionButtonClick(event: Event, entity: any): void {
    console.log('Action button clicked!', event, entity);

    event.preventDefault();
    event.stopPropagation();

    // Set the selected row for context menu
    this.selectedRowCM = entity;
    console.log('Selected row set:', this.selectedRowCM);

    // Find the row element and trigger the context menu on it
    const button = event.target as HTMLElement;
    const row = button.closest('tr');

    console.log('Button element:', button);
    console.log('Row element:', row);

    if (row) {
      // Create a context menu event at the button position
      const contextMenuEvent = new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: button.getBoundingClientRect().left,
        clientY: button.getBoundingClientRect().bottom + 5
      });

      console.log('Context menu event created:', contextMenuEvent);
      console.log('Event coordinates:', contextMenuEvent.clientX, contextMenuEvent.clientY);

      // Dispatch the context menu event on the row
      row.dispatchEvent(contextMenuEvent);
      console.log('Context menu event dispatched on row');
    } else {
      console.warn('Could not find row element');
    }
  }

  private loadPreferencesFromService(): void {
    if (!this.tableId) return;

    const preferences = this.shareTableService.getPreferences(this.tableId);

    if (preferences) {
      if (preferences.visibleColumns?.length) {
        this.visibleColumns = preferences.visibleColumns;
      } else if (this.columns.length > 0) {
        this.visibleColumns = this.columns.map((col) => col.field);
      }

      if (preferences.rowsPerPage) {
        this._metaData.rows = preferences.rowsPerPage;
      }

      if (preferences.globalFilter !== undefined) {
        this._metaData.globalFilter = preferences.globalFilter;
        this.globalFilterValue = preferences.globalFilter;
      }

      if (preferences.sortField) {
        this._metaData.sort = `${preferences.sortField}:${preferences.sortOrder ?? 1}`;
        this.sortField = preferences.sortField;
        this.sortOrder = preferences.sortOrder ?? 1;
      }

      this.activeFilters = preferences.activeFilters ?? [];

      if (preferences.filterLogic) {
        this.filterLogic = preferences.filterLogic;
      }
    } else if (this.columns.length > 0) {
      this.visibleColumns = this.columns.map((col) => col.field);
    }
  }

  loadDataLazy($event: LazyLoadEvent) {
    this.firstLazyLoadCompleted = true;
    this.syncColumnFilters($event.filters);

    const sort = this.convertSortFieldAndOrder($event.sortField || '', $event.sortOrder || 1);
    const newFilterStr = translateFiltersToQueryParams(this.activeFilters, this.filterLogic).join(
      '|'
    );
    const oldFilterStr = (this.metaData.filter ?? []).join('|');

    // Use globalFilterValue (bound to input via ngModel) as the authoritative source.
    // PrimeNG emits $event.globalFilter = '' on the initial lazy load even when the
    // input field shows a restored value, which would silently clear the persisted filter.
    const globalFilter = this.globalFilterValue ?? $event.globalFilter ?? '';

    const hasChanged =
      this.metaData.first !== $event.first ||
      this.metaData.rows !== $event.rows ||
      this.metaData.globalFilter !== globalFilter ||
      this.metaData.sort !== sort ||
      oldFilterStr !== newFilterStr;

    if (hasChanged) {
      this.metaData.loading = true;
      this.data.next([]);

      if (this.tableId) {
        this.shareTableService.updatePreferences(this.tableId, {
          rowsPerPage: $event.rows || 100,
          globalFilter: globalFilter,
          sortField: $event.sortField as string,
          sortOrder: $event.sortOrder || 1
        });
      }

      this.metaDataChange.emit({
        totalRecords: this.totalRecords,
        first: $event.first ?? 0,
        rows: $event.rows ?? 100,
        loading: true,
        globalFilter: globalFilter,
        sort: sort,
        filter: translateFiltersToQueryParams(this.activeFilters, this.filterLogic),
        id: ''
      });
    }
  }

  /**
   * Merges PrimeNG column filter state into activeFilters chips.
   * Preserves manually-added 'builder' filters; replaces 'column' filters
   * with whatever PrimeNG currently reports.
   */
  private syncColumnFilters(
    primeFilters: { [s: string]: FilterMetadata | FilterMetadata[] } | undefined
  ): void {
    const builderFilters = this.activeFilters.filter((f) => f.source === 'builder');
    if (!primeFilters) {
      this.activeFilters = builderFilters;
      return;
    }

    // Collect non-global column filters reported by PrimeNG this cycle
    const reportedColumnFilters: ActiveFilter[] = [];
    const reportedFields = new Set<string>();
    for (const [field, meta] of Object.entries(primeFilters)) {
      if (field === 'global') continue;
      reportedFields.add(field);
      const conditions: FilterMetadata[] = Array.isArray(meta) ? meta : [meta];
      for (const cond of conditions) {
        const val = cond.value != null ? String(cond.value).trim() : '';
        if (!val) continue;
        const col = this.columns.find((c) => c.field === field);
        const matchMode = cond.matchMode ?? 'contains';
        const op = this.operatorOptions.find((o) => o.value === matchMode);
        reportedColumnFilters.push({
          field,
          fieldLabel: col?.header ?? field,
          operator: matchMode,
          operatorLabel: op?.label ?? matchMode,
          value: val,
          source: 'column'
        });
      }
    }

    // Keep existing column filters for fields PrimeNG didn't report this cycle
    // (happens when only the global filter changed and PrimeNG only emits {global:…})
    const unreportedColumnFilters = this.activeFilters.filter(
      (f) => f.source === 'column' && !reportedFields.has(f.field)
    );

    this.activeFilters = [...builderFilters, ...unreportedColumnFilters, ...reportedColumnFilters];
  }

  filterMenuItem() {
    console.log('filter menu item clicked', this.filter);
    this.selectedFilterObservable$.emit({
      id: this.filter
    });
  }

  addFilter(): void {
    if (!this.pendingField || !this.pendingOperator || !this.pendingValue.trim()) return;
    const col = this.columns.find((c) => c.field === this.pendingField);
    const op = this.operatorOptions.find((o) => o.value === this.pendingOperator);
    this.activeFilters = [
      ...this.activeFilters,
      {
        field: this.pendingField,
        fieldLabel: col?.header ?? this.pendingField,
        operator: this.pendingOperator,
        operatorLabel: op?.label ?? this.pendingOperator,
        value: this.pendingValue.trim(),
        source: 'builder' as const
      }
    ];
    this.pendingValue = '';
    this.saveFilterState();
    this.emitWithFilters();
  }

  removeFilter(index: number): void {
    const removed = this.activeFilters[index];
    this.activeFilters = this.activeFilters.filter((_, i) => i !== index);
    this.saveFilterState();

    if (removed.source === 'column' && this.primeTable) {
      this.primeTable.filter(null, removed.field, removed.operator);
    } else {
      this.emitWithFilters();
    }
  }

  clearGlobalFilter(): void {
    this.globalFilterValue = '';
    if (this.primeTable) {
      this.primeTable.filterGlobal('', 'contains');
    }
  }

  onFilterLogicChange(): void {
    this.saveFilterState();
    if (this.activeFilters.length > 0) {
      this.emitWithFilters();
    }
  }

  private saveFilterState(): void {
    if (!this.tableId) return;
    this.shareTableService.updatePreferences(this.tableId, {
      activeFilters: this.activeFilters.filter((f) => f.source === 'builder'),
      filterLogic: this.filterLogic
    });
  }

  /**
   * Moves the paginator back to page 1 without emitting a load - for a
   * parent that changes what the table shows outside share-table's own
   * controls (e.g. a quick-view button) and reloads with offset 0 itself.
   * p-table keeps its own `first`, so setting metaData.first alone leaves
   * the paginator on the old page.
   */
  resetToFirstPage(): void {
    this._metaData.first = 0;
    if (this.primeTable) this.primeTable.first = 0;
  }

  private emitWithFilters(): void {
    this.metaData.loading = true;
    // This load is for offset 0 - keep the paginator in step with it.
    if (this.primeTable) this.primeTable.first = 0;
    this.data.next([]);
    this.metaDataChange.emit({
      totalRecords: this.totalRecords,
      first: 0,
      rows: this.metaData.rows,
      loading: true,
      globalFilter: this.metaData.globalFilter ?? '',
      sort: this.metaData.sort ?? '',
      filter: translateFiltersToQueryParams(this.activeFilters, this.filterLogic),
      id: this.metaData.id ?? ''
    });
  }

  onRowSelect($event: any) {
    // console.log('the following row has been selected', this.selectedRow);
    this.selectedRowObservable$.emit(this.selectedRow);
  }

  onPaymentDialog(entity: any) {
    this.showPaymentDialog.emit({
      selectedRow: entity,
      showDialog: true
    });
  }

  onContextMenuSelect($event: any) {
    // console.log('the following row has been selected', this.selectedRowCM);
    console.log('the following row has been selected', $event);

    this.selectedRowCMObservable$.emit($event.data);
  }

  onRowUnselect($event: any) {
    // Deliberately does not re-emit selectedRowObservable$: in a
    // selectionMode="single" table, clicking a different row fires
    // onRowUnselect (for the previously-selected row) immediately before
    // onRowSelect (for the newly-clicked row). Emitting the "open dialog"
    // signal here as well as from onRowSelect meant every such click fired
    // it twice - once for the row being deselected, once for the row
    // actually clicked - opening two dialogs stacked on top of each other.
  }

  onContextMenu($event: any) {
    // Handle context menu event - this is called when right-clicking on a row
    console.log('Context menu triggered:', $event);
    console.log('Current menu items:', this.menuItems);
    this.selectedRowCM = $event.data;
  }

  getNumberOfColumns(): number {
    // Return the number of visible columns, or total columns if no visibility filter is applied
    return this.visibleColumns?.length > 0 ? this.visibleColumns.length : this.columns?.length || 0;
  }

  getFilterInputValue(e: Event): any {
    const inputelement = e.target as HTMLInputElement;
    this.filteringFor = 'Filtering for: ' + inputelement.value;
    if (inputelement.value === '') {
      this.filteringFor = '';
    }
    return inputelement.value;
  }

  getNestedValue(obj: any, path: string) {
    let returnObject = {
      value: null as any,
      type: '',
      nested: false,
      plural: false,
      pluralField: '',
      items: [] as any[]
    };

    returnObject.value = path.split('.').reduce((acc, part) => acc && acc[part], obj);

    returnObject.nested = path.includes('.');

    returnObject.type = typeof returnObject.value;

    if (Array.isArray(returnObject.value)) {
      returnObject.plural = true;
      returnObject.nested = true;
      // A related entity that's since been deleted comes back as a null
      // array entry rather than being omitted - drop those here so the
      // template never has to render a button for a relation that no
      // longer resolves to anything.
      returnObject.items = returnObject.value.filter((item) => item != null);
      returnObject.pluralField = path.split('.')[1];
      returnObject.type = 'array';
    } else if (typeof returnObject.value === 'object' && returnObject.value !== null) {
      returnObject.plural = false;
      returnObject.items = [returnObject.value];
      returnObject.type = 'object';
    } else if (typeof returnObject.value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(returnObject.value)) {
        returnObject.type = returnObject.value.length > 10 ? 'datetime' : 'date';
      }
    }

    return returnObject;
  }

  getColumnFilterType(col: TableColumns): string {
    // determine type of filter to use and derive type of value
    if (col.type) {
      return col.type;
    } else {
      return 'text';
    }
  }

  clearFilterInputValue() {
    const inputelement = document.getElementsByName('globalFilter')[0] as HTMLInputElement;
    inputelement.value = '';
  }

  clearTableFilters(table: any) {
    const activeTab = this.route.snapshot.params['activeTab'];
    const activeSubTab = this.route.snapshot.params['activeSubTab'];
    // Other modules (e.g. Services) also route through a param literally named
    // "activeTab" for their own tabs, so its mere presence doesn't mean we're
    // in Operations - confirm against the actual URL before taking the
    // Operations-specific navigation path below.
    const isOperationsRoute = this.router.url.startsWith('/operations');

    this.selectedRow = null;
    this.selectedRowCM = null;
    // Reset globalFilterValue before table.clear() so that the onLazyLoad
    // event it triggers picks up '' rather than the old value.
    this.globalFilterValue = '';
    if (this.tableId) {
      this.shareTableService.updatePreferences(this.tableId, { globalFilter: '' });
    }
    table.clear();
    this.clearFilterInputValue();
    this.filterValue = '';
    this.filteringFor = '';
    this.queryParams = '';
    this.activeFilters = [];
    this.filterLogic = 'AND';
    this.pendingField = '';
    this.pendingOperator = 'contains';
    this.pendingValue = '';
    this.filterBuilderVisible = false;
    this.saveFilterState();

    // clear the query params
    // Handle both old and new Operations routing patterns
    if (isOperationsRoute && activeSubTab && activeTab === 'management') {
      // New routing pattern for management sub-tabs
      this.router.navigate(['/operations/management', activeSubTab], {
        queryParams: {}, // Remove all query params
        queryParamsHandling: 'merge'
      });
    } else if (isOperationsRoute && activeTab) {
      // Old routing pattern for main tabs
      this.router.navigate(['/operations', activeTab], {
        queryParams: {}, // Remove all query params
        queryParamsHandling: 'merge'
      });
    } else {
      // share-table is also used outside the Operations module, which sends
      // navigation there off the rails - hardcoding a navigate to
      // '/operations/...' sent the user out of whatever page they were
      // actually on. Just drop this route's own query params instead.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        queryParamsHandling: ''
      });
    }

    // reload table data
    this.metaDataChange.emit({
      totalRecords: this.totalRecords,
      first: 0,
      rows: 100,
      loading: true,
      globalFilter: '',
      sort: '',
      filter: [],
      id: ''
    });
  }

  getAccountSeverity(status: string): string {
    return statusSeverity(status);
  }

  getSubscriptionSeverity(status: string): string {
    return statusSeverity(status);
  }

  getServiceSeverity(status: string): string {
    return statusSeverity(status);
  }

  getTechnicianStatusSeverity(status: string): string {
    switch (status) {
      case 'active':
        return 'text-green-500';
      case 'on_break':
        return 'text-orange-500';
      case 'offline':
        return 'text-red-500';
      case 'in_progress':
        return 'text-blue-500';
      case 'scheduled':
        return 'text-purple-500';
      case 'completed':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  }

  unselectRow() {
    this.selectedRow = null; // Ensure this is bound to the table's selection
  }

  convertSortFieldAndOrder(sortField: string, sortOrder: number): string {
    return sortOrder === 1 ? sortField : `-${sortField}`;
  }

  convertToQueryParamsObject(filters: FilterObject | undefined): string[] {
    if (!filters) {
      return [];
    }

    const matchModeMap: { [key: string]: string } = {
      contains: 'cn',
      startsWith: 'sw',
      equals: 'eq',
      notEqual: 'ne',
      lessThan: 'lt',
      // dateBefore: 'lt',
      greaterThan: 'gt',
      // dateGreater: 'gt',
      lessThanOrEqual: 'le',
      greaterThanOrEqual: 'ge',
      in: 'in',
      notIn: 'ni'
    };

    const queryParams: string[] = [];

    for (const [field, filterArray] of Object.entries(filters)) {
      for (const filter of filterArray) {
        if (filter.value !== null && matchModeMap[filter.matchMode]) {
          const op = matchModeMap[filter.matchMode];

          const paramValue = `${field}:${op}:${filter.value}`;
          queryParams.push(paramValue);
        }
      }
    }

    return queryParams;
  }

  getSeverityFromBool(booleanValue: boolean) {
    if (booleanValue) {
      return 'success';
    } else {
      return 'danger';
    }
  }

  getDevicePhysicalStatus(device: any) {
    if (device['physical_status'] === 'active' || device['device_physical_status'] === 'active') {
      return 'pi-globe';
    } else if (device['physical_status'] === 'los' || device['physical_status'] === 'los') {
      return 'pi-sort-alt-slash';
    } else if (
      device['physical_status'] === 'inactive' ||
      device['physical_status'] === '' ||
      device['device_physical_status'] === 'inactive' ||
      device['device_physical_status'] === ''
    ) {
      return 'pi-ban';
    } else {
      return 'pi-ban';
    }
  }

  getDevicePhysicalStatusColor(device: any) {
    if (device['physical_status'] === 'active' || device['device_physical_status'] === 'active') {
      return 'green';
    } else if (device['physical_status'] === 'los' || device['physical_status'] === 'los') {
      return 'orange';
    } else if (
      device['physical_status'] === 'inactive' ||
      device['physical_status'] === '' ||
      device['device_physical_status'] === 'inactive' ||
      device['device_physical_status'] === ''
    ) {
      return 'red';
    } else {
      return 'red';
    }
  }

  getTransactionStatusSeverity(status: any): string {
    return statusSeverity(status, 'danger');
  }

  onNestedObjectClick(entity: any, path: string) {
    console.log('nested object clicked');
    console.log('entity', entity);
    console.log('path', path);
    this.selectedRelatedEntityObservable$.emit({
      entity: entity,
      path: path
    });
  }

  getPluralNestedField(item: any) {
    if (item['name']) {
      return item['name'];
    } else if (item['model']) {
      return item['model'];
    } else {
      return 'N/A';
    }
  }

  downloadFile(toc_file_reference: string) {
    this.api
      .getFileAPI('download', toc_file_reference)
      .subscribe((response: HttpResponse<Blob>) => {
        const mimetype = 'application/pdf';
        const file = response.body;
        if (!file) return;
        const downloadFile = new File([file], `${toc_file_reference}.pdf`, {
          type: mimetype
        });
        const url = window.URL.createObjectURL(downloadFile);
        window.open(url, '_blank');
        URL.revokeObjectURL(url);
      });
  }

  downloadAs(format: 'csv' | 'xlsx'): void {
    if (!this.downloadEndpoint || this.downloading) return;

    this.downloading = true;

    const params: Record<string, unknown> = { format };
    const q = this.metaData.globalFilter?.trim();
    if (q) params['q'] = q;
    const sort = this.metaData.sort?.trim();
    if (sort) params['sort'] = sort;
    const filters = [
      ...translateFiltersToQueryParams(this.activeFilters, this.filterLogic),
      ...(this.extraFilters || [])
    ];
    if (filters.length) params['filter'] = filters;

    const mimeType =
      format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';

    this.api.downloadCsvAPI(this.downloadEndpoint, params).subscribe({
      next: (response: HttpResponse<Blob>) => {
        const blob = new Blob([response.body as Blob], { type: mimeType });
        const disposition: string = response.headers?.get('Content-Disposition') ?? '';
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        const fallback = `${this.downloadEndpoint!.split('/').pop()}_export.${format}`;
        const filename = match?.[1] ?? fallback;

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
        this.downloading = false;
      },
      error: () => {
        this.downloading = false;
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onVisibleColumnsChange(_event: unknown): void {
    if (this.tableId) {
      this.shareTableService.updatePreferences(this.tableId, {
        visibleColumns: this.visibleColumns
      });
    }
    this.cdr.detectChanges();
  }
}
